import { useState, useEffect, useRef, useCallback } from 'react';
import { Paper, Text, Group, Button, Select, Modal, Badge } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconLock, IconLockOpen, IconRefresh } from '@tabler/icons-react';
import apiService from '../services/api';
import VatField from './VatField';
import './AdminVatReport.css';

const QUARTER_OPTIONS = [
  { value: '1', label: 'Q1 / Jan – Mar' },
  { value: '2', label: 'Q2 / Apr – Jun' },
  { value: '3', label: 'Q3 / Jul – Sep' },
  { value: '4', label: 'Q4 / Oct – Dec' }
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const y = CURRENT_YEAR - 5 + i;
  return { value: String(y), label: String(y) };
});

function fmt(v) {
  if (v == null || isNaN(v)) return '0.00';
  return Number(v).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SummaryBox({ amount, label }) {
  return (
    <div className="vat-summary-box">
      <div className="vat-summary-box__label">{label}</div>
      <div className="vat-summary-box__amount">{fmt(amount)}</div>
    </div>
  );
}

export default function AdminVatReport() {
  const m = new Date().getMonth();
  const defaultQuarter = String(m <= 2 ? 1 : m <= 5 ? 2 : m <= 8 ? 3 : 4);
  const [quarter, setQuarter] = useState(defaultQuarter);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const dirtyRef = useRef({});
  const saveTimerRef = useRef(null);

  const fetchReport = useCallback(async () => {
    if (!quarter || !year) return;
    setLoading(true);
    try {
      const data = await apiService.getAccountingVatReport(quarter, year);
      setReport(data);
      dirtyRef.current = {};
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [quarter, year]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const flushOverrides = useCallback(async () => {
    const keys = Object.keys(dirtyRef.current);
    if (keys.length === 0) return;
    const payload = {};
    for (const k of keys) payload[k] = dirtyRef.current[k];
    dirtyRef.current = {};
    setSaveStatus('saving');
    try {
      const updated = await apiService.updateAccountingVatOverrides(quarter, year, payload);
      setReport(updated);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus('error');
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  }, [quarter, year]);

  const handleFieldChange = useCallback((code, value) => {
    dirtyRef.current[code] = value;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushOverrides, 500);
  }, [flushOverrides]);

  const handleRegenerate = async () => {
    setRegenerateOpen(false);
    try {
      const updated = await apiService.saveAccountingVatReport(quarter, year);
      setReport(updated);
      notifications.show({ title: 'Regenerated', message: 'Report recomputed from approved documents.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleSubmit = async () => {
    try {
      await flushOverrides();
      const updated = await apiService.submitAccountingVatReport(quarter, year);
      setReport(updated);
      notifications.show({ title: 'Submitted', message: 'VAT report has been closed.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleReopen = async () => {
    try {
      const updated = await apiService.reopenAccountingVatReport(quarter, year);
      setReport(updated);
      notifications.show({ title: 'Reopened', message: 'VAT report returned to draft.', color: 'blue' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handlePrint = () => window.print();

  const isDraft = report?.status === 'draft';
  const isSubmitted = report?.status === 'submitted';

  return (
    <Paper
      p="md"
      className="vat-report-container"
      component="main"
      aria-labelledby="vat-report-title"
    >
      <Group justify="space-between" mb="md" className="no-print">
        <div>
          <Text id="vat-report-title" component="h1" size="md" fw={700} className="vat-main-title">VAT (MWST)</Text>
          {report && (
            <Group gap="xs" mt={2}>
              <Text size="sm" c="dimmed">{report.meta.periodLabel}</Text>
              <Badge size="sm" color={isSubmitted ? 'green' : 'yellow'} variant="filled">
                {isSubmitted ? 'Submitted' : 'Draft'}
              </Badge>
              {saveStatus === 'saving' ? <Text size="xs" c="dimmed" role="status" aria-live="polite">Saving...</Text> : null}
              {saveStatus === 'saved' ? <Text size="xs" c="green" role="status" aria-live="polite">Saved</Text> : null}
              {saveStatus === 'error' ? <Text size="xs" c="red" role="alert">Save failed</Text> : null}
            </Group>
          )}
        </div>
        {report && (
          <SummaryBox amount={report.revenue.field200?.value ?? report.revenue.field200} label="Umsatz CHF" />
        )}
        <Group gap="sm">
          <Select size="xs" data={QUARTER_OPTIONS} value={quarter} onChange={setQuarter} w={130} aria-label="Reporting quarter" />
          <Select size="xs" data={YEAR_OPTIONS} value={year} onChange={setYear} w={90} aria-label="Reporting year" />
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={handlePrint}>
            Print / PDF
          </Button>
        </Group>
      </Group>

      {loading ? (
        <Text role="status" aria-live="polite" c="dimmed" ta="center" py="xl">Loading...</Text>
      ) : !report ? (
        <Text role="status" aria-live="polite" c="dimmed" ta="center" py="xl">Select a quarter and year to generate the VAT report.</Text>
      ) : (
        <div className="vat-report" id="vat-report-content">
          <VatReportBody report={report} onChange={handleFieldChange} />

          <Group justify="space-between" mt="md" className="no-print">
            <Group gap="sm">
              {isDraft && (
                <>
                  <Button size="xs" variant="light" color="orange" leftSection={<IconRefresh size={14} />} onClick={() => setRegenerateOpen(true)}>
                    Regenerate
                  </Button>
                  <Button size="xs" variant="filled" color="green" leftSection={<IconLock size={14} />} onClick={handleSubmit}>
                    Mark quarter as closed
                  </Button>
                </>
              )}
              {isSubmitted && (
                <Button size="xs" variant="light" color="yellow" leftSection={<IconLockOpen size={14} />} onClick={handleReopen}>
                  Reopen
                </Button>
              )}
            </Group>
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={handlePrint}>
              Print / PDF
            </Button>
          </Group>
        </div>
      )}

      <Modal opened={regenerateOpen} onClose={() => setRegenerateOpen(false)} title="Regenerate VAT Report?" size="sm">
        <Text size="sm" mb="md">This will:</Text>
        <Text size="sm" component="ul" ml="lg" mb="md">
          <li>Recompute all values from your approved documents</li>
          <li>Remove all manual overrides</li>
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={() => setRegenerateOpen(false)}>Cancel</Button>
          <Button color="orange" onClick={handleRegenerate}>Regenerate</Button>
        </Group>
      </Modal>
    </Paper>
  );
}

function VatReportBody({ report, onChange }) {
  const r = report;
  const dd = r.revenue.deductions;

  function v(field) {
    return field && typeof field === 'object' ? field : { value: field ?? 0, computed: field ?? 0, override: false };
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div className="vat-form-box">
        <div className="vat-form__section">
          <div className="vat-form__left">
            <div className="vat-form__title">I. UMSATZ</div>
            <div className="vat-form__row">
              <div className="vat-form__desc">Total agreed / received consideration</div>
              <div className="vat-form__digit">200</div>
              <div className="vat-form__amount vat-form__amount--large">{fmt(r.revenue.field200?.value ?? r.revenue.field200)}</div>
            </div>

            {[
              { label: 'Tax-exempt income', code: '205', key: 'field205' },
              { label: 'Deductions', code: '220', key: 'field220' },
              { label: 'Foreign services', code: '221 +', key: 'field221' },
              { label: 'Reporting procedure', code: '225 +', key: 'field225' },
              { label: 'Non-taxable services', code: '230 +', key: 'field230' },
              { label: 'Price reductions', code: '235 +', key: 'field235' },
              { label: 'Miscellaneous', code: '280 +', key: 'field280' },
            ].map(({ label, code, key }) => (
              <div className="vat-form__row small" key={key}>
                <div className="vat-form__desc">{label}</div>
                <div className="vat-form__digit">{code}</div>
                <div className="vat-form__amount"><span className="vat-small-box"><VatField field={v(dd[key])} onChange={onChange} /></span></div>
              </div>
            ))}

            <div className="vat-form__row total">
              <div className="vat-form__desc">Total</div>
              <div className="vat-form__digit"></div>
              <div className="vat-form__amount vat-form__amount--total">{fmt(r.revenue.field289?.value ?? r.revenue.field289)}</div>
            </div>

            <div className="vat-form__row total big">
              <div className="vat-form__desc">Taxable turnover</div>
              <div className="vat-form__digit">299</div>
              <div className="vat-form__amount vat-form__amount--large">{fmt(r.revenue.field299?.value ?? r.revenue.field299)}</div>
            </div>
          </div>
        </div>

        <div className="vat-form__section vat-form__section--tax">
          <div className="vat-form__title">II. Tax calculation</div>

          <div className="vat-tax-grid">
            <div className="vat-tax-grid__head">
              <div className="vat-tax-grid__col">FROM 01.01.2024</div>
              <div className="vat-tax-grid__col">UNTIL 31.12.2023</div>
            </div>

            <div className="vat-tax-rows">
              <div className="vat-tax-row">
                <div className="vat-tax-label">Normal</div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field303.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field303.vat)} onChange={onChange} /></span>
                  <span className="vat-rate">{r.outputVat.current.field303.rate}%</span>
                </div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field302.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field302.vat)} onChange={onChange} /></span>
                  <span className="vat-rate">{r.outputVat.historical.field302.rate}%</span>
                </div>
              </div>
              <div className="vat-tax-row">
                <div className="vat-tax-label">Reduced</div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field313.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field313.vat)} onChange={onChange} /></span>
                  <span className="vat-rate">{r.outputVat.current.field313.rate}%</span>
                </div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field312.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field312.vat)} onChange={onChange} /></span>
                  <span className="vat-rate">{r.outputVat.historical.field312.rate}%</span>
                </div>
              </div>
              <div className="vat-tax-row">
                <div className="vat-tax-label">Lodging</div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field343.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field343.vat)} onChange={onChange} /></span>
                  <span className="vat-rate">{r.outputVat.current.field343.rate}%</span>
                </div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field342.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field342.vat)} onChange={onChange} /></span>
                  <span className="vat-rate">{r.outputVat.historical.field342.rate}%</span>
                </div>
              </div>
              <div className="vat-tax-row">
                <div className="vat-tax-label">Import tax</div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field383.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.current.field383.vat)} onChange={onChange} /></span>
                </div>
                <div className="vat-tax-values">
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field382.taxable)} onChange={onChange} /></span>
                  <span className="vat-op">+</span>
                  <span className="vat-small-box"><VatField field={v(r.outputVat.historical.field382.vat)} onChange={onChange} /></span>
                </div>
              </div>
            </div>

            <div className="vat-tax-total">Total tax owed = {fmt(r.outputVat.field399?.value ?? r.outputVat.field399)}</div>

            <div className="vat-inputs">
              {[
                { label: 'Input tax on materials', key: 'field400' },
                { label: 'Input tax on investments', key: 'field405' },
                { label: 'Acquisition tax', key: 'field410' },
                { label: 'Corrections', key: 'field415' },
                { label: 'Reductions', key: 'field420' },
              ].map(({ label, key }) => (
                <div className="vat-input-row" key={key}>
                  <span className="vat-label">{label}</span>
                  <span className="vat-code">{key.replace('field', '')}</span>
                  <span className="vat-small-box"><VatField field={v(r.inputVat[key])} onChange={onChange} /></span>
                </div>
              ))}

              <div className="vat-input-row total">
                <span className="vat-label">Total deductible input tax</span>
                <span className="vat-code">479</span>
                <span className="vat-small-box">{fmt(r.inputVat.field479?.value ?? r.inputVat.field479)}</span>
              </div>

              <div className="vat-input-row total big">
                <span className="vat-label">Amount payable to tax authority</span>
                <span className="vat-code">500</span>
                <span className="vat-small-box">{fmt(r.vatPayable.field500?.value ?? r.vatPayable.field500)}</span>
              </div>
              <div className="vat-input-row total big">
                <span className="vat-label">Credit balance</span>
                <span className="vat-code">510</span>
                <span className="vat-small-box">{fmt(r.vatPayable.field510?.value ?? r.vatPayable.field510)}</span>
              </div>
            </div>
          </div>

          <div className="vat-form__section vat-form__section--other">
            <div className="vat-form__title">III. Other cash flows</div>
            <div className="vat-input-row"><span className="vat-label">Subsidies / tourism charges / etc.</span><span className="vat-code">900</span><span className="vat-small-box">0.00</span></div>
            <div className="vat-input-row"><span className="vat-label">Donations / dividends / damages</span><span className="vat-code">910</span><span className="vat-small-box">0.00</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}


